import React from "react";
import {
	Button,
	Divider,
	Grid,
	Paper,
	Tooltip,
	Typography,
	IconButton,
} from "@mui/material";
import { Box, alpha } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import useStyles from "./styles";
import {
	DevicesOtherOutlined,
	EditNoteOutlined,
	StorageOutlined,
	VpnKeyOutlined,
	ContentCopy,
	OpenInNew,
	VpnLockOutlined,
	HttpsOutlined,
	Close,
	Check,
	Clear,
} from "@mui/icons-material";

// 0 = VM/Servidor, 1 = DB, 2 = Web, 3 = VPN
const TYPE_OPTIONS = [
	{ label: "VM / Servidor", value: 0 },
	{ label: "Banco de Dados", value: 1 },
	{ label: "Acesso Web", value: 2 },
	{ label: "VPN", value: 3 },
];

export default function PreviewData({
	acesso,
	setSnackbar,
	onEdit,
	setOpenDrawer,
	windowWidth,
}) {
	const theme = useTheme();
	const styles = useStyles(theme);
	async function handleCopy(text, label) {
		if (!text) return;
		try {
			await navigator.clipboard.writeText(text);
		} catch (err) {
			const textArea = document.createElement("textarea");
			textArea.value = text;
			document.body.appendChild(textArea);
			textArea.select();
			try {
				document.execCommand("copy");
			} catch (error) {
				console.error("Falha ao copiar", err);
			}
			document.body.removeChild(textArea);
		}
		if (setSnackbar) {
			setSnackbar({
				children: `${label} copiado!`,
				severity: "success",
			});
		}
	}

	// Componente auxiliar para renderizar cada campo
	const InfoField = ({
		label,
		value,
		copyable = true,
		isLink = false,
		xs = 12,
		sm = 6,
		md = 3,
	}) => {
		if (!value && value !== 0) return null;

		return (
			<Grid item xs={xs} sm={sm} md={md}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						p: 1,
						borderRadius: 1,
						"&:hover": {
							bgcolor: alpha(theme.palette.primary.main, 0.04),
							cursor: copyable ? "pointer" : "default",
						},
					}}
					onClick={() => copyable && handleCopy(value, label)}
				>
					<Typography variant="caption" color="text.secondary" fontWeight={600}>
						{label}
					</Typography>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Typography
							variant="body2"
							sx={{
								wordBreak: "break-all",
								fontWeight: 500,
								color: isLink ? "primary.main" : "text.primary",
							}}
						>
							{value}
						</Typography>
						{isLink && (
							<Tooltip title="Abrir link">
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										window.open(
											value.startsWith("http") ? value : `https://${value}`,
											"_blank",
										);
									}}
								>
									<OpenInNew fontSize="inherit" />
								</IconButton>
							</Tooltip>
						)}
						{copyable && !isLink && (
							<ContentCopy sx={{ fontSize: 12, opacity: 0.5 }} color="action" />
						)}
					</Box>
				</Box>
			</Grid>
		);
	};

	// Label do Tipo para o Header
	const tipoAtual = Number(acesso?.tipo);
	const tipoLabel =
		acesso?.tags?.map((tag) => tag.name).join(", ") ||
		TYPE_OPTIONS.find((opt) => opt.value === tipoAtual)?.label;

	return (
		<Grid item xs={12}>
			{/* --- HEADER FIXO (Sempre visível) --- */}
			<Box sx={styles.boxHeader}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							{windowWidth < 900 && (
								<IconButton
									onClick={() => {
										setOpenDrawer(false);
									}}
								>
									<Close />
								</IconButton>
							)}
							<Typography variant="subtitle1" fontWeight={700}>
								{acesso?.titulo || "Sem Título"}
							</Typography>
						</Box>
						<Typography variant="caption" color="text.secondary">
							{tipoLabel || "Não classificado"} •{" "}
							{acesso?.ip || acesso?.dbHost || "IP não informado"}
						</Typography>
					</Box>

					<Button
						variant="contained"
						size="small"
						startIcon={<HttpsOutlined />}
						onClick={(e) => {
							e.stopPropagation();
							if (onEdit) onEdit();
						}}
					>
						Editar
					</Button>
				</Box>
			</Box>

			<Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
				{/* --- TIPO 0: VM / SERVIDOR --- */}
				{tipoAtual === 0 && (
					<Box mb={3}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
							<DevicesOtherOutlined color="primary" fontSize="small" />
							<Typography variant="subtitle2" fontWeight={700}>
								VM / Servidor
							</Typography>
						</Box>
						<Grid container spacing={1}>
							<InfoField label="IP / Host" value={acesso?.ip} />
							<InfoField label="Porta SSH" value={acesso?.sshPort} />
							<InfoField label="Usuário SSH" value={acesso?.sshUser} />
							<InfoField label="Senha SSH" value={acesso?.sshPass} />
							<InfoField label="Senha Root" value={acesso?.rootPass} />
							<InfoField
								label="SSH"
								value={`ssh ${acesso?.sshUser}@${acesso?.ip}`}
							/>
						</Grid>
						<Divider sx={{ my: 2 }} />
					</Box>
				)}

				{/* --- TIPO 1: BANCO DE DADOS --- */}
				{tipoAtual === 1 && (
					<Box mb={3}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
							<StorageOutlined color="primary" fontSize="small" />
							<Typography variant="subtitle2" fontWeight={700}>
								Banco de Dados
							</Typography>
						</Box>
						<Grid container spacing={1}>
							<InfoField label="Host" value={acesso?.dbHost} />
							<InfoField label="Porta" value={acesso?.dbPort} />
							<InfoField label="Database" value={acesso?.dbName} />
							<InfoField label="Usuário DB" value={acesso?.dbUser} />
							<InfoField label="Senha DB" value={acesso?.dbPass} />
						</Grid>
						<Divider sx={{ my: 2 }} />
					</Box>
				)}

				{/* --- TIPO 2: ACESSOS WEB (Array no JSON) --- */}
				{tipoAtual === 2 && (
					<Box mb={3}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
							<VpnKeyOutlined color="primary" fontSize="small" />
							<Typography variant="subtitle2" fontWeight={700}>
								Acessos Web / Links
							</Typography>
						</Box>

						{/* Iterar sobre o array web_accesses do JSON */}
						{acesso?.web_accesses?.length > 0 ? (
							acesso.web_accesses.map((webItem, index) => (
								<Box
									key={index}
									sx={{
										mb: 2,
										p: 1.5,
										bgcolor: alpha(theme.palette.action.hover, 0.05),
										borderRadius: 1,
									}}
								>
									<Grid container spacing={1}>
										<InfoField
											label="URL"
											value={webItem.url}
											isLink={true}
											md={4}
										/>
										<InfoField label="Login" value={webItem.login} md={4} />
										<InfoField label="Senha" value={webItem.pass} md={4} />
									</Grid>
								</Box>
							))
						) : (
							<Typography variant="body2" color="text.secondary">
								Nenhum link cadastrado.
							</Typography>
						)}
						<Divider sx={{ my: 2 }} />
					</Box>
				)}

				{/* --- TIPO 3: VPN --- */}
				{tipoAtual === 3 && (
					<Box mb={3}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
							<VpnLockOutlined color="primary" fontSize="small" />
							<Typography variant="subtitle2" fontWeight={700}>
								VPN / Tunelamento
							</Typography>
						</Box>
						<Grid container spacing={1}>
							<InfoField label="Nome da VPN" value={acesso?.vpnName} />
							<InfoField label="Gateway (Server)" value={acesso?.vpnGateway} />
							<InfoField label="Usuário" value={acesso?.vpnUser} />
							<InfoField label="Senha" value={acesso?.vpnPass} />

							{/* Campos Específicos de VPN baseados no JSON */}
							{acesso?.enableIPsec && (
								<InfoField
									label="Chave Pré-Comp. (PSK)"
									value={acesso?.vpnPSK}
								/>
							)}

							<InfoField label="Domínio NT" value={acesso?.vpnNTDomain} />

							{/* Opcional: Mostrar booleanos como texto */}

							<Grid item xs={12}>
								<Box sx={{ display: "flex", gap: 2, mt: 1 }}>
									{acesso?.useL2TPEphemeral ? (
										<Box
											sx={{
												display: "flex",
												bgcolor: `${theme.palette.chip.background.success}`,
												px: 1,
												borderRadius: 0.5,
												alignItems: "center",
											}}
										>
											<Check
												fontSize="16px"
												sx={{
													mr: 1,
													mb: 0.2,
													color: `${theme.palette.chip.colorText.success}`,
												}}
											/>
											<Typography
												variant="caption"
												sx={{
													color: `${theme.palette.chip.colorText.success}`,
													fontWeight: 600,
													mt: 0.2,
												}}
											>
												L2TP Ephemeral
											</Typography>
										</Box>
									) : (
										<Box
											sx={{
												display: "flex",
												bgcolor: `${theme.palette.chip.background.critical}`,
												px: 1,
												borderRadius: 0.5,
												alignItems: "center",
											}}
										>
											<Close
												fontSize="16px"
												sx={{
													mr: 1,
													mb: 0.2,
													color: `${theme.palette.chip.colorText.critical}`,
												}}
											/>
											<Typography
												variant="caption"
												sx={{
													color: `${theme.palette.chip.colorText.critical}`,
													fontWeight: 600,
													mt: 0.2,
												}}
											>
												L2TP Ephemeral
											</Typography>
										</Box>
									)}
									{acesso?.enableIPsec ? (
										<Box
											sx={{
												display: "flex",
												bgcolor: `${theme.palette.chip.background.success}`,
												px: 1,
												borderRadius: 0.5,
												alignItems: "center",
											}}
										>
											<Check
												fontSize="16px"
												sx={{
													mr: 1,
													mb: 0.2,
													color: `${theme.palette.chip.colorText.success}`,
												}}
											/>
											<Typography
												variant="caption"
												sx={{
													color: `${theme.palette.chip.colorText.success}`,
													fontWeight: 600,
													mt: 0.2,
												}}
											>
												IPSec Ativo
											</Typography>
										</Box>
									) : (
										<Box
											sx={{
												display: "flex",
												bgcolor: `${theme.palette.chip.background.critical}`,
												px: 1,
												borderRadius: 0.5,
												alignItems: "center",
											}}
										>
											<Clear
												fontSize="16px"
												sx={{
													mr: 1,
													mb: 0.2,
													color: `${theme.palette.chip.colorText.critical}`,
												}}
											/>
											<Typography
												variant="caption"
												sx={{
													color: `${theme.palette.chip.colorText.critical}`,
													fontWeight: 600,
													mt: 0.2,
												}}
											>
												IPSec Desativado
											</Typography>
										</Box>
									)}
								</Box>
							</Grid>
						</Grid>
						<Divider sx={{ my: 2 }} />
					</Box>
				)}

				{acesso?.observacoes && (
					<Box>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								mb: 1,
							}}
						>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<EditNoteOutlined color="primary" fontSize="small" />
								<Typography variant="subtitle2" fontWeight={700}>
									Anotações / Observações
								</Typography>
							</Box>
							<Tooltip title="Copiar observações">
								<IconButton
									size="small"
									onClick={() => handleCopy(acesso?.observacoes, "Observações")}
								>
									<ContentCopy fontSize="small" color="action" />
								</IconButton>
							</Tooltip>
						</Box>

						<Box
							onClick={() => handleCopy(acesso?.observacoes, "Observações")}
							sx={{
								p: 1.5,
								bgcolor: alpha(theme.palette.background.default, 0.5),
								borderRadius: 1,
								border: `1px dashed ${theme.palette.divider}`,
								cursor: "pointer",
								transition: "background-color 0.2s",
								"&:hover": {
									bgcolor: alpha(theme.palette.primary.main, 0.04),
								},
							}}
						>
							<Typography
								variant="body2"
								sx={{
									whiteSpace: "pre-wrap",
									wordBreak: "break-word",
									fontFamily: "monospace",
								}}
							>
								{acesso?.observacoes}
							</Typography>
						</Box>
					</Box>
				)}
			</Paper>
		</Grid>
	);
}
